import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge, Banner, Button, Sheet, TextArea, cn } from '@dd/ui';
import { errText } from '@/lib/api';

export function PageHead({ title, sub, right }: { title: string; sub?: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0"><h1 className="text-[26px] font-extrabold leading-tight tracking-tight text-ink">{title}</h1>{sub && <p className="mt-1 max-w-3xl text-sm text-ink-3">{sub}</p>}</div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

export const Panel = ({ title, right, children, className }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) => (
  <section className={cn('rounded-2xl border border-line/80 bg-white shadow-[0_1px_2px_rgb(16_24_40/0.04)]', className)}>
    {(title || right) && <header className="flex items-center justify-between gap-2 border-b border-line/80 px-5 py-3.5"><h2 className="text-[15px] font-bold text-ink">{title}</h2>{right}</header>}
    <div className="p-5">{children}</div>
  </section>
);

export const Loading = ({ label = 'Loading…' }: { label?: string }) => <div className="flex items-center justify-center gap-2 py-10 text-ink-3"><Loader2 className="animate-spin" size={18} />{label}</div>;
export const Empty = ({ children }: { children: ReactNode }) => <div className="py-10 text-center text-sm text-ink-3">{children}</div>;
export const ErrorBox = ({ error, retry }: { error: unknown; retry?: () => void }) => <Banner tone="bad" action={retry ? <Button size="sm" variant="outline" onClick={retry}>Retry</Button> : undefined}>{errText(error)}</Banner>;

export interface Col<T> { key: string; head: ReactNode; cell: (r: T) => ReactNode; className?: string }
export function Table<T>({ cols, rows, onRow, empty = 'Nothing here.', rowKey }: { cols: Col<T>[]; rows: T[]; onRow?: (r: T) => void; empty?: string; rowKey: (r: T) => string }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  const [first, ...rest] = cols;
  return (
    <>
    {/* phones: one card per row — the first column is the title, the others are label / value pairs, so nothing hides off to the right */}
    <ul className="space-y-2.5 md:hidden">
      {rows.map((r) => (
        <li key={rowKey(r)} onClick={onRow ? () => onRow(r) : undefined} className={cn('rounded-xl border border-line/80 bg-white p-3.5', onRow && 'cursor-pointer active:bg-slate-50')}>
          <div className="text-sm font-semibold text-ink">{first!.cell(r)}</div>
          {rest.length > 0 && (
            <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              {rest.map((c) => (
                <div key={c.key} className="min-w-0">
                  {typeof c.head === 'string' && c.head.trim() !== '' && <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-3">{c.head}</dt>}
                  <dd className="min-w-0 break-words text-ink">{c.cell(r)}</dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ul>
    <div className="hidden overflow-x-auto md:block">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-line bg-slate-50/80 text-[11px] uppercase tracking-wider text-ink-3">{cols.map((c) => <th key={c.key} className={cn('px-3 py-2.5 font-semibold first:rounded-tl-lg last:rounded-tr-lg', c.className)}>{c.head}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={rowKey(r)} onClick={onRow ? () => onRow(r) : undefined} className={cn('border-b border-line/70 last:border-0', onRow ? 'cursor-pointer hover:bg-brand-50/70' : 'hover:bg-slate-50/60')}>
            {cols.map((c) => <td key={c.key} className={cn('px-3 py-2.5 align-top', c.className)}>{c.cell(r)}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
    </>
  );
}

export const LoadMore = ({ has, loading, onClick }: { has?: boolean; loading?: boolean; onClick: () => void }) => (has ? <div className="pt-3 text-center"><Button variant="outline" size="sm" loading={loading} onClick={onClick}>Load more</Button></div> : null);

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'brand' | 'neutral'> = {
  active: 'ok', succeeded: 'ok', completed: 'ok', approved: 'ok', resolved: 'ok', applied: 'ok', delivered: 'ok', sent: 'ok', allowed: 'ok', ok: 'ok',
  pending: 'warn', requested: 'warn', processing: 'warn', initiated: 'warn', trial: 'brand', open: 'warn', pending_customer: 'brand', pending_internal: 'warn', previewed: 'warn', cooldown: 'warn', pending_approval: 'warn', flagged: 'warn', past_due: 'warn', cooling_off: 'warn', queued: 'warn',
  failed: 'bad', rejected: 'bad', suspended: 'bad', disabled: 'bad', blocked: 'bad', cancelled: 'bad', refunded: 'brand', partially_refunded: 'brand', free: 'neutral', closed: 'neutral', dismissed: 'neutral',
};
export const StatusBadge = ({ s }: { s: string }) => <Badge tone={STATUS_TONE[s] ?? 'neutral'}>{s.replace(/_/g, ' ')}</Badge>;

/** Every Admin write needs a reason of at least 5 characters (Guide §17.3); it lands in the audit log. */
export function ReasonSheet({ open, onClose, title, intro, confirm = 'Confirm', danger, children, onSubmit, needReason = true }: {
  open: boolean; onClose: () => void; title: string; intro?: ReactNode; confirm?: string; danger?: boolean; children?: ReactNode; needReason?: boolean;
  onSubmit: (reason: string) => Promise<unknown> | void;
}) {
  const [reason, setReason] = useState(''); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const ok = !needReason || reason.trim().length >= 5;
  const go = async () => {
    setBusy(true); setErr(null);
    try { await onSubmit(reason.trim()); setReason(''); onClose(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title={title} footer={<Button size="lg" block variant={danger ? 'danger' : 'primary'} loading={busy} disabled={!ok} onClick={() => void go()}>{confirm}</Button>}>
      <div className="space-y-3">
        {intro && <p className="text-sm text-ink-2">{intro}</p>}
        {err && <Banner tone="bad">{err}</Banner>}
        {children}
        {needReason && <TextArea label="Reason (audited, min 5 characters)" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
      </div>
    </Sheet>
  );
}

export const KV = ({ k, v }: { k: string; v: ReactNode }) => <div className="flex justify-between gap-3 py-1.5 text-sm"><span className="text-ink-3">{k}</span><span className="text-right font-medium text-ink">{v}</span></div>;
export const Tabs = <T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: Array<{ value: T; label: string; badge?: number }> }) => (
  <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">{items.map((i) => (
    <button key={i.value} type="button" onClick={() => onChange(i.value)} className={cn('relative flex h-10 items-center gap-1.5 whitespace-nowrap px-3.5 text-sm font-semibold transition-colors', value === i.value ? 'text-brand after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand' : 'text-ink-3 hover:text-ink')}>{i.label}{!!i.badge && <span className="rounded-full bg-brand px-1.5 text-[11px] font-bold leading-5 text-white">{i.badge}</span>}</button>
  ))}</div>
);

const AV = ['#D12B29', '#2563EB', '#0D9488', '#7C3AED', '#C2410C', '#BE185D', '#1E3A8A', '#65A30D'];
export function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const n = name.trim() || '?';
  const c = AV[[...n].reduce((a, ch) => a + ch.charCodeAt(0), 0) % AV.length]!;
  return <span className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white" style={{ width: size, height: size, background: c, fontSize: size * 0.4 }}>{[...n][0]!.toUpperCase()}</span>;
}

const ROLE_STYLE: Record<string, string> = {
  super_admin: 'bg-brand text-white', support: 'bg-sky-100 text-sky-800', finance: 'bg-emerald-100 text-emerald-800', content: 'bg-violet-100 text-violet-800', custom: 'bg-amber-100 text-amber-800',
};
export const ROLE_LABEL: Record<string, string> = { super_admin: 'Super Admin', support: 'Support', finance: 'Finance', content: 'Content', custom: 'Custom' };
export const RoleBadge = ({ role }: { role: string }) => <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide', ROLE_STYLE[role] ?? 'bg-neutral-100 text-ink-2')}>{ROLE_LABEL[role] ?? role}</span>;

/** a tiny inline trend line (no chart library): values left → right */
export function Spark({ values, stroke = '#D12B29', fill = true, w = 120, h = 36 }: { values: number[]; stroke?: string; fill?: boolean; w?: number; h?: number }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1); const min = Math.min(...values, 0);
  const x = (i: number) => (i / (values.length - 1)) * w;
  const y = (v: number) => h - 3 - ((v - min) / (max - min || 1)) * (h - 6);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="overflow-visible" aria-hidden>
      {fill && <path d={`${d} L${w},${h} L0,${h} Z`} fill={stroke} opacity={0.1} />}
      <path d={d} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** a statistic card; `tone` paints the icon chip, `to`/`onClick` make it a shortcut */
export function Kpi({ icon: Icon, label, value, sub, tone, spark, sparkColor }: { icon: React.ComponentType<{ size?: number }>; label: string; value: ReactNode; sub?: ReactNode; tone?: 'bad' | 'warn' | 'ok'; spark?: number[]; sparkColor?: string }) {
  return (
    <div className="flex h-full flex-col justify-between gap-3 rounded-2xl border border-line/80 bg-white p-4 shadow-[0_1px_2px_rgb(16_24_40/0.04)] transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-3">{label}</div>
          <div className="mt-1 text-[28px] font-extrabold leading-none tracking-tight text-ink tabular">{value}</div>
        </div>
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', tone === 'bad' ? 'bg-bad-bg text-bad' : tone === 'warn' ? 'bg-warn-bg text-warn' : tone === 'ok' ? 'bg-ok-bg text-ok' : 'bg-brand-50 text-brand')}><Icon size={20} /></span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-xs text-ink-3">{sub}</div>
        {spark && <Spark values={spark} stroke={sparkColor} w={96} h={30} />}
      </div>
    </div>
  );
}

export const Chip = ({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'bad' | 'brand' | 'info' }) => (
  <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', tone === 'ok' ? 'bg-ok-bg text-ok' : tone === 'warn' ? 'bg-warn-bg text-warn' : tone === 'bad' ? 'bg-bad-bg text-bad' : tone === 'brand' ? 'bg-brand-50 text-brand-dark' : tone === 'info' ? 'bg-sky-100 text-sky-800' : 'bg-neutral-100 text-ink-2')}>{children}</span>
);
